import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Stack,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  IconButton,
  TableContainer,
  CircularProgress,
} from '@mui/material';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { enqueueSnackbar } from 'notistack';
import { useForm, Controller, useFormContext } from 'react-hook-form';
import InputMask from 'react-input-mask';

import FormProvider from 'src/components/hook-form';
import Scrollbar from 'src/components/scrollbar';
import Iconify from 'src/components/iconify';
import UpdatedText from 'src/components/ui-kit/updated-text';
import { UserLevel, UserLevelType } from 'src/type/user';
import { UserLevelPrice } from 'src/type/user-level-price';
import { ProductType } from 'src/type/product';
import { toastError } from 'src/utils/format-error';
import axios, { endpoints } from 'src/utils/axios';
import { Pagination } from 'src/type/utils';
import { fNumber } from 'src/utils/format-number';

type Props = {
  levelProfit: UserLevelType[];
  forceReload: () => void;
};

export default function SettingUserLevelProfit({ levelProfit, forceReload }: Props) {
  const [userLevelPrices, setUserLevelPrices] = useState<UserLevelPrice[]>([]);
  const [products, setProducts] = useState<ProductType[]>([]);
  const [userLevels, setUserLevels] = useState<UserLevelType[]>([]);
  const [editingCell, setEditingCell] = useState<{
    levelId: UserLevel;
    productId: string;
    field: 'buyProfit' | 'sellProfit';
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const latestUpdatedAt = useMemo(() => {
    const allUpdates = [
      ...userLevelPrices.map((ulp) => ({
        time: new Date(ulp.updatedAt || ulp.createdAt).getTime(),
        user: ulp.updatedByUser,
      })),
    ];

    if (allUpdates.length === 0) {
      return { time: Date.now(), user: undefined };
    }

    const latestUpdate = allUpdates.reduce((latest, current) =>
      current.time > latest.time ? current : latest
    );

    return {
      time: latestUpdate.time,
      user: latestUpdate.user
        ? `${latestUpdate.user.firstName} ${latestUpdate.user.lastName}`
        : 'admin',
    };
  }, [userLevelPrices]);

  // Helper function to get price data for a specific level and product
  const getPriceData = (levelId: UserLevel, productId: string): UserLevelPrice | undefined => {
    const priceData = userLevelPrices.find(
      (price) => price.userLevelId === levelId && price.productId === productId
    );
    return priceData;
  };

  const handleSave = useCallback(
    async (
      levelId: UserLevel,
      productId: string,
      field: 'buyProfit' | 'sellProfit',
      value: number
    ) => {
      if (isUpdating) {
        return; // Prevent multiple calls
      }

      const existingPrice = userLevelPrices.find(
        (price) => price.userLevelId === levelId && price.productId === productId
      );

      if (!existingPrice) {
        toastError('Price data not found');
        return;
      }

      setIsUpdating(true);

      const updatedPrice: Partial<UserLevelPrice> = {
        ...existingPrice,
        [field]: value,
      };

      try {
        // Update local state immediately for better UX
        const updatedUserLevelPrices = userLevelPrices.map((ulp) =>
          ulp.id === existingPrice.id ? updatedPrice : ulp
        );
        setUserLevelPrices(updatedUserLevelPrices as UserLevelPrice[]);

        // Update via API
        await axios.patch(endpoints.userLevelPrice.update(existingPrice.id), {
          [field]: value,
        });

        setEditingCell(null);
        enqueueSnackbar('บันทึกสำเร็จ', { variant: 'success' });
      } catch (error) {
        console.error('Error updating user level price:', error);
        toastError(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [userLevelPrices, isUpdating]
  );

  const getData = useCallback(async () => {
    try {
      const [{ data: productList }, { data: userLevelList }, { data: userLevelPriceList }] =
        await Promise.all([
          axios.get<Pagination<ProductType>>(endpoints.product.get),
          axios.get(endpoints.userLevel.get),
          axios.get(endpoints.userLevelPrice.get),
        ]);

      const sortedProducts = productList.results.sort((a, b) => a.sequence - b.sequence);
      setProducts(sortedProducts);
      setUserLevels(userLevelList);
      setUserLevelPrices(userLevelPriceList);
    } catch (error) {
      console.error('Error fetching data:', error);
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  return (
    <Card sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack>
            <Typography variant="h6">ส่วนต่างราคา (ต่อบาท)</Typography>
            <Typography variant="caption" color="text.secondary">
              <UpdatedText
                user={latestUpdatedAt.user}
                updatedAt={new Date(latestUpdatedAt.time).toISOString()}
              />
            </Typography>
          </Stack>
        </Stack>

        <TableContainer sx={{ position: 'relative', overflow: 'auto' }}>
          <Scrollbar ref={scrollContainerRef}>
            <Table sx={{ minWidth: 320 * products.length + 200 }}>
              <TableHead>
                <TableRow>
                  {/* Main header row */}
                  <TableCell
                    align="center"
                    rowSpan={2}
                    sx={{
                      fontWeight: 'fontWeightSemiBold',
                      backgroundColor: 'background.neutral',
                      width: 70,
                      borderRight: '1px solid',
                      borderRightColor: 'divider',
                    }}
                  >
                    ระดับ
                  </TableCell>
                  <TableCell
                    align="center"
                    rowSpan={2}
                    sx={{
                      fontWeight: 'fontWeightSemiBold',
                      backgroundColor: 'background.neutral',
                      width: 120,
                      borderRight: '1px solid',
                      borderRightColor: 'divider',
                    }}
                  >
                    ลูกค้า
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'background.neutral',
                      fontSize: 18,
                    }}
                  >
                    ส่วนต่างราคา
                  </TableCell>
                </TableRow>
                <TableRow>
                  {/* Sub header row */}
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'fontWeightSemiBold',
                      p: 0,
                    }}
                  >
                    <Box sx={{ display: 'flex' }}>
                      {products.map((product, productIndex) => {
                        const isNotLastProduct = productIndex < products.length - 1;

                        return (
                          <Box
                            key={product.id}
                            sx={{
                              minWidth: 320,
                              flex: 1,
                              p: 1,
                              textAlign: 'center',
                              backgroundColor: 'background.neutral',
                              ...(isNotLastProduct && {
                                borderRight: '1px solid',
                                borderRightColor: 'divider',
                              }),
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 'fontWeightSemiBold' }}
                            >
                              {product.name}
                              {!product.isEnable && (
                                <Typography
                                  variant="caption"
                                  sx={{ ml: 0.5, color: 'error.main' }}
                                  component="span"
                                >
                                  (ปิดอยู่)
                                </Typography>
                              )}
                            </Typography>
                            <Box sx={{ display: 'flex', mt: 0.5 }}>
                              <Box
                                sx={{
                                  flex: 1,
                                  py: 0.5,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 'fontWeightMedium' }}
                                >
                                  รับซื้อ
                                </Typography>
                              </Box>
                              <Box sx={{ flex: 1, py: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 'fontWeightMedium' }}
                                >
                                  ขาย
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 10 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && userLevels.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 10 }}>
                      <Typography variant="body2" color="text.secondary">
                        ไม่มีข้อมูล
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  userLevels.length > 0 &&
                  products.length > 0 &&
                  userLevels.map((level) => (
                    <TableRow key={level.id} sx={{ height: 55 }}>
                      <TableCell align="center" sx={{ p: 0 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRight: '1px solid',
                            borderRightColor: 'divider',
                          }}
                        >
                          {level.name}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 0 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRight: '1px solid',
                            borderRightColor: 'divider',
                          }}
                        >
                          {level.description}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: 1 }}>
                          {products.map((product, productIndex) => {
                            const priceData = getPriceData(level.id, product.id);
                            const isNotLastProduct = productIndex < products.length - 1;

                            return (
                              <Box
                                key={product.id}
                                sx={{
                                  minWidth: 320,
                                  flex: 1,
                                  p: 1,
                                  height: 1,
                                  ...(isNotLastProduct && {
                                    borderRight: '1px solid',
                                    borderRightColor: 'divider',
                                  }),
                                }}
                              >
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {/* Buy Profit */}
                                  <Box sx={{ flex: 1 }}>
                                    {editingCell?.levelId === level.id &&
                                    editingCell?.productId === product.id &&
                                    editingCell?.field === 'buyProfit' ? (
                                      <ProfitInputField
                                        value={priceData?.buyProfit || 0}
                                        onSave={(value) =>
                                          handleSave(level.id, product.id, 'buyProfit', value)
                                        }
                                        onCancel={() => setEditingCell(null)}
                                      />
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          cursor: 'pointer',
                                          '&:hover': {
                                            backgroundColor: 'action.hover',
                                            borderRadius: 1,
                                            px: 1,
                                          },
                                        }}
                                        onClick={() => {
                                          setEditingCell({
                                            levelId: level.id,
                                            productId: product.id,
                                            field: 'buyProfit',
                                          });
                                        }}
                                      >
                                        {fNumber(priceData?.buyProfit || 0)}
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Sell Profit */}
                                  <Box sx={{ flex: 1 }}>
                                    {editingCell?.levelId === level.id &&
                                    editingCell?.productId === product.id &&
                                    editingCell?.field === 'sellProfit' ? (
                                      <ProfitInputField
                                        value={priceData?.sellProfit || 0}
                                        onSave={(value) =>
                                          handleSave(level.id, product.id, 'sellProfit', value)
                                        }
                                        onCancel={() => setEditingCell(null)}
                                      />
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          cursor: 'pointer',
                                          '&:hover': {
                                            backgroundColor: 'action.hover',
                                            borderRadius: 1,
                                            px: 1,
                                          },
                                        }}
                                        onClick={() => {
                                          setEditingCell({
                                            levelId: level.id,
                                            productId: product.id,
                                            field: 'sellProfit',
                                          });
                                        }}
                                      >
                                        {fNumber(priceData?.sellProfit || 0)}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        {/* Navigation arrows */}
        <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
          <IconButton
            onClick={handleScrollLeft}
            sx={{
              backgroundColor: 'background.neutral',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Iconify icon="eva:arrow-ios-back-fill" />
          </IconButton>
          <IconButton
            onClick={handleScrollRight}
            sx={{
              backgroundColor: 'background.neutral',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Iconify icon="eva:arrow-ios-forward-fill" />
          </IconButton>
        </Stack>
      </Stack>
    </Card>
  );
}

// Inline editing component
function ProfitInputField({
  value: initialValue,
  onSave,
  onCancel,
}: {
  value: number;
  onSave: (value: number) => void;
  onCancel: () => void;
}) {
  const [inputValue, setInputValue] = useState(initialValue.toString());
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    const numValue = parseFloat(inputValue);
    if (!Number.isNaN(numValue)) {
      setIsSaving(true);
      try {
        await onSave(numValue);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault(); // Prevent any default behavior
      onCancel();
    }
  };

  const methods = useForm({
    defaultValues: {
      profitValue: inputValue,
    },
  });

  // Update form value when inputValue changes
  useEffect(() => {
    methods.setValue('profitValue', inputValue);
  }, [inputValue, methods]);

  return (
    <FormProvider methods={methods}>
      <CustomTextField
        name="profitValue"
        size="small"
        autoFocus
        type="number"
        disabled={isSaving}
        sx={{ width: 80 }}
        onKeyDown={handleKeyDown}
        onBlur={handleSave}
        onChange={(event) => {
          const { value } = event.target;
          setInputValue(value);
        }}
        InputProps={{
          endAdornment: isSaving && <CircularProgress size={16} sx={{ mr: 1 }} />,
        }}
      />
    </FormProvider>
  );
}

// Keep the CustomTextField component for potential future use
type CustomTextFieldProps = TextFieldProps & {
  name: string;
  mask?: string;
};

function CustomTextField({
  name,
  helperText,
  mask,
  type,
  inputMode,
  ...other
}: CustomTextFieldProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        if (mask) {
          return (
            <InputMask mask={mask} {...field} maskChar="">
              {() => (
                <TextField
                  fullWidth
                  error={!!error}
                  helperText={error ? error?.message : helperText}
                  {...other}
                />
              )}
            </InputMask>
          );
        }

        return (
          <TextField
            {...field}
            fullWidth
            type={type}
            value={field.value || ''}
            onChange={(event) => {
              const { value } = event.target;

              // Only allow: -, 0-9, and decimal point with max 3 decimal places
              const isValidFormat = /^-?\d*\.?\d{0,3}$/.test(value);
              const minusCount = (value.match(/-/g) || []).length;

              if (minusCount <= 1 && isValidFormat) {
                // Convert -.123 to -0.123 and .123 to 0.123
                let processedValue = value;
                if (value.startsWith('-.') && value.length > 2) {
                  processedValue = value.replace('-.', '-0.');
                } else if (value.startsWith('.') && value.length > 1) {
                  processedValue = value.replace('.', '0.');
                }
                field.onChange(processedValue);
              }
            }}
            onKeyDown={(e) => {
              // Prevent form submission on Enter
              if (e.key === 'Enter') {
                e.preventDefault();
                return;
              }

              // Only allow: -, 0-9, ., Backspace, Delete, Arrow keys, Tab
              const allowedKeys = [
                '-',
                '0',
                '1',
                '2',
                '3',
                '4',
                '5',
                '6',
                '7',
                '8',
                '9',
                '.',
                'Backspace',
                'Delete',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Tab',
                'Home',
                'End',
              ];

              if (!allowedKeys.includes(e.key)) {
                e.preventDefault();
                return;
              }

              // Allow minus sign only at the beginning
              if (e.key === '-') {
                const currentValue = (e.target as HTMLInputElement).value;
                const cursorPosition = (e.target as HTMLInputElement).selectionStart;

                // Allow minus sign only if cursor is at position 0 and no minus sign exists
                if (cursorPosition !== 0 || currentValue.includes('-')) {
                  e.preventDefault();
                }
              }

              // Allow decimal point only once
              if (e.key === '.') {
                const currentValue = (e.target as HTMLInputElement).value;
                if (currentValue.includes('.')) {
                  e.preventDefault();
                }
              }
            }}
            error={!!error}
            helperText={error ? error?.message : helperText}
            {...other}
          />
        );
      }}
    />
  );
}
